import { useParams } from 'react-router-dom'
import produce from 'immer'
import { findIndex } from 'lodash-es'
import { useDebouncedCallback } from 'use-debounce'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getProjectFiles } from '@queries'
import { postFileMetadata } from '@mutations'

interface BaseFile {
  name: string
  item_guid: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useFileMetadata = <T extends Record<string, any> = Record<string, any>>(
  rootGuid: string,
  options?: {
    optimisticUpdate?: boolean
    debounceDelay?: number
  },
) => {
  const { projectId } = useParams()
  const queryClient = useQueryClient()

  const debouncedPostrequest = useDebouncedCallback(
    postFileMetadata.request,
    options?.debounceDelay || 0,
  )

  return useMutation(
    async (config: BaseFile & T) => {
      return await debouncedPostrequest(projectId, rootGuid, config)
    },
    {
      onMutate: config => {
        if (options?.optimisticUpdate) {
          const key = getProjectFiles.getKey(projectId, rootGuid)
          const currentData = queryClient.getQueryData<FilesResult>(key) as FilesResult

          const newData = produce(currentData, draft => {
            const index = findIndex(draft.files, file => file.guid === config.item_guid)
            if (index !== -1) {
              draft.files[index].additional_data = config
            }
          })

          queryClient.setQueryData(key, newData)
        }
      },
    },
  )
}

export default useFileMetadata
